import { PropertyValueMap, TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property, queryAssignedNodes, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { repeat } from 'lit/directives/repeat.js';
import { nanoid } from 'nanoid';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksThumbnailSelect } from '../../events/blocks-gallery-thumbnail-select';
import { MOBILE_BREAK_POINT } from '../../util/common-modal';
import { KEYS } from '../../util/event-keys';
import { scrollIntoView } from '../../util/scroll';
import { BlocksElement } from '../BlocksElement';
import '../image-gallery-item/image-gallery-item';
import { BlocksImageGalleryItem } from '../image-gallery-item/image-gallery-item';
import '../image-gallery-thumbnail-item/image-gallery-thumbnail-item';
import { BlocksImageGalleryThumbnailItem } from '../image-gallery-thumbnail-item/image-gallery-thumbnail-item';
import '../image-gallery-thumbnail/image-gallery-thumbnail';
import { BlocksImageGalleryThumbnail } from '../image-gallery-thumbnail/image-gallery-thumbnail';
import type { ImageGallery, ImageGridPosition, ImageObjectFit, ImageSize } from './image-gallery.model';
import styles from './image-gallery.styles';

let thumbnailId = 1;
/**
 * @fires bl-thumbnail-select -  Emitted when user select the thumbnail
 *
 * @csspart base - The component's base wrapper.
 * @csspart thumbnails-container - The component's thumbnails container.
 * @csspart thumbnails-wrapper - The component's thumbnails wrapper.
 * @csspart thumbnails-link-wrapper - The component's thumbnails link wrapper.
 * @csspart thumbnails-link - The component's thumbnails link.
 * @csspart grid-item-wrapper - The component's grid-item wrapper.
 * @csspart grid-item - The component's grid item.
 * @csspart thumbnail-image - The component's thumbnail image.
 * @csspart text-container - The component's text container.
 * @csspart text - The component's text container.
 * @csspart image-container - The component's image container.
 * @csspart gallery-item - The component's image gallery item.
 */
export class BlocksImageGallery extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * mobile breakpoint property for small view
   * Defaults to 768px.
   */
  @property({ type: Number })
  mobileBreakPoint = MOBILE_BREAK_POINT;

  /**
   * Size
   */
  @property({ type: String })
  size: ImageSize = 'default';

  /**
   * Array of images for image gallery
   */
  @property({ type: Array })
  images: Array<ImageGallery> = [];

  /**
   * Grid position prop
   */
  @property({ type: String })
  gridPosition: ImageGridPosition = 'left';

  @property({ type: String })
  objectFit: ImageObjectFit;

  /**
   * property to hide thumbnails
   * 1) This property is not applicable for new NextJS Image Gallery implementation
   */
  @property({ type: Boolean })
  hideThumbnails = false;

  /**
   * property to hide thumbnails
   * 1) This property is not applicable for new NextJS Image Gallery implementation
   */
  @property({ type: Boolean })
  hideThumbnailText = false;

  /**
   * Active gallery thumbnail item state
   * 1) Sets the initial active thumbnail item default is 0
   **/
  @property({ type: Number })
  activeIndex = 0;

  /**
   * ariaTablistLabel for customized label announcements for tablist.
   */
  @property({ type: String })
  ariaTablistLabel: string | undefined;

  /**
   * state isMobile set based on mobile breakpoint
   */
  @state()
  private isMobile: boolean | undefined;

  /**
   * state to maintain selected image
   */
  @state()
  private selectedItem: ImageGallery | undefined;

  /**
   * selectedIndex is used to track active item
   */
  @state() private selectedIndex = 0;

  /**
   * Query all the thumbnail gallery
   */
  @queryAssignedNodes({ flatten: false })
  private galleryThumbnails: Array<BlocksImageGalleryThumbnail> | undefined;

  /**
   * Active thumbnail item state
   */
  @state()
  private activeThumbnailItem: BlocksImageGalleryThumbnailItem | undefined;

  /**
   * Active gallery item state
   */
  @state()
  private activeGalleryItem: BlocksImageGalleryItem | undefined;

  protected slots = new SlotController(this, null);

  /**
   * Auto Increment id to sync thumbnail item and gallery item
   */
  private thumbnailId: string;

  /**
   * Keyboard navigation keys for Image Gallery. Default is Up and Down arrow key for
   * prev and next respectively.
   */
  private readonly navigationKeys = {
    prev: KEYS.UP_ARROW,
    next: KEYS.DOWN_ARROW
  };

  /**
   * Query all the gallery thumbnail items in the image gallery
   *
   * This getter method first attempts to find thumbnail items within the light DOM
   * using the selector 'cat-image-gallery-thumbnail-item'. If found, it returns these items.
   *
   * If no items are found in the light DOM, it then searches within the shadow DOM
   * using the same selector. If found, it returns these items.
   *
   * Additionally, if the component is in a mobile view or the size is set to 'sm',
   * it searches for elements with the class 'cat-c-image-gallery__icon-link_wrapper'
   * within the shadow DOM and returns these items.
   *
   * If no items are found in any of the above searches, it returns an empty array.
   *
   * @returns {Array<BlocksImageGalleryThumbnailItem>} An array of thumbnail items.
   */
  private get _thumbnailItems(): Array<BlocksImageGalleryThumbnailItem> {
    const lightDomItems = Array.from(this.querySelectorAll('cat-image-gallery-thumbnail-item'));
    if (lightDomItems.length) {
      return lightDomItems;
    }
    const shadowDomItems = Array.from(this.shadowRoot?.querySelectorAll('cat-image-gallery-thumbnail-item') || []);
    if (shadowDomItems.length) {
      return shadowDomItems;
    }

    const smallVariantItems = Array.from(this.shadowRoot?.querySelectorAll('.cat-c-image-gallery__icon-link_wrapper') || []);
    if ((this.isMobile || this.size === 'sm') && smallVariantItems.length) {
      return smallVariantItems as BlocksImageGalleryThumbnailItem[];
    }
    return [];
  }

  /**
   * Query all the gallery thumbnail items  for the non custom gallery
   * In case of mobile and small variant,  need to query the span class
   */
  private get _thumbnailItemsDefault(): Array<BlocksImageGalleryThumbnailItem> | undefined {
    const imageGalleryIconLink = this.shadowRoot?.querySelectorAll('.cat-c-image-gallery__icon-link');
    const imageGalleryThumbnailItem = this.shadowRoot?.querySelectorAll('cat-image-gallery-thumbnail-item');

    if (this.isMobile || this.size === 'sm') {
      if (imageGalleryIconLink) {
        return Array.from(imageGalleryIconLink) as Array<BlocksImageGalleryThumbnailItem>;
      }
    } else if (imageGalleryThumbnailItem) {
      return Array.from(imageGalleryThumbnailItem) as Array<BlocksImageGalleryThumbnailItem>;
    }
  }

  /**
   * Query the gallery thumbnail in the image gallery
   */
  private get _thumbnail(): BlocksImageGalleryThumbnail | null {
    return this.shadowRoot?.querySelector('cat-image-gallery-thumbnail') || this.querySelector('cat-image-gallery-thumbnail');
  }

  /**
   * Query all the gallery thumbnail items in the image gallery.
   * This getter first attempts to find `cat-image-gallery-item` elements within the light DOM.
   * If any are found, they are returned as an array.
   * If no elements are found in the light DOM, it then attempts to find `cat-image-gallery-item` elements within the shadow DOM.
   * If any are found in the shadow DOM, they are returned as an array.
   * If no elements are found in either the light DOM or shadow DOM, an empty array is returned.
   * @returns {Array<BlocksImageGalleryItem>} An array of `BlocksImageGalleryItem` elements.
   */
  private get _galleryItems(): Array<BlocksImageGalleryItem> {
    const lightDomItems = Array.from(this.querySelectorAll('cat-image-gallery-item'));
    if (lightDomItems.length) {
      return lightDomItems;
    }
    const shadowDomItems = this.shadowRoot ? Array.from(this.shadowRoot.querySelectorAll('cat-image-gallery-item')) : [];
    if (shadowDomItems.length) {
      return shadowDomItems;
    }
    return [];
  }

  constructor() {
    super();
    this.thumbnailId = 'cat-thumbnail-item' + thumbnailId++;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.setMobile);
  }

  protected async firstUpdated() {
    await this.updateComplete;
    this.createGallery();
  }

  private syncImageGalleryWithItem(): void {
    this._thumbnailItems.forEach((thumbnailItem, index) => {
      const ariaLabelledBy = thumbnailItem.ariaLabelledBy;
      const id = ariaLabelledBy || nanoid();
      thumbnailItem.ariaLabelledBy = id; /* 1 */
      thumbnailItem.index = index;
      thumbnailItem.isActive = false;
      const controlId = this.thumbnailId + `-${index}${ariaLabelledBy ? '-' + ariaLabelledBy : ''}`;
      thumbnailItem.ariaControls = controlId;
      thumbnailItem.setAttribute('tabindex', '-1');
      const galleryItem = this._galleryItems[index];
      if (galleryItem) {
        galleryItem.setAttribute('id', controlId);
      }
    });
  }

  private activateImageGallery(): void {
    if (this.activeThumbnailItem && this.activeGalleryItem) {
      this.activeThumbnailItem.isActive = true;
      this.activeThumbnailItem.setAttribute('tabindex', '0');
      this.activeGalleryItem.isActive = true;
      this.activeGalleryItem.setAttribute('tabindex', '0');

      if (this._galleryItems.length === 1) {
        this.activeGalleryItem.setAttribute('id', this.activeThumbnailItem.ariaControls ?? this.activeGalleryItem.id);
      }

      this.scrollIntoViewThumbnail();
      this.focusOnActiveThumbnail();
    }
  }

  /**
   * Sets focus on the currently active thumbnail item in the image gallery.
   * If there is no active thumbnail item, this method does nothing.
   *
   * @private
   */
  private focusOnActiveThumbnail(): void {
    this.activeThumbnailItem?.focus();
  }

  private async createGallery() {
    this.syncImageGalleryWithItem();
    this.activeThumbnailItem = this._thumbnailItems[this.activeIndex];
    this.activeGalleryItem = this._galleryItems[this.activeIndex];
    this.activateImageGallery();
  }

  /**
   * Updated lifecycle
   */
  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('activeIndex') && this.activeIndex != _changedProperties.get('activeIndex')) {
      if (this.activeThumbnailItem) {
        this.deactivatePreviouslySelectedItem();
      }
      this.activeThumbnailItem = this._thumbnailItems[this.activeIndex];
      this.activeGalleryItem = this.updateActiveGalleryItem();
      this.activateImageGallery();
      if (this.slots.isEmpty()) {
        this.setGalleryItem(this.images[this.activeIndex], this.activeIndex);
      }
    }
    if (_changedProperties.has('images')) {
      this.onPageInit();
    }
    if (_changedProperties.has('gridPosition')) {
      this.setGridPosition();
      this.setNavigationKeys();
    }
    if (_changedProperties.has('isMobile') || _changedProperties.has('size')) {
      this.setSmallView();
      this.setGridPosition();
      this.setNavigationKeys();
    }
  }

  private updateActiveGalleryItem() {
    return this._galleryItems.length === 1 ? this._galleryItems[0] : this._galleryItems[this.activeIndex];
  }

  private deactivatePreviouslySelectedItem(): void {
    if (this.activeThumbnailItem) {
      this.activeThumbnailItem.isActive = false;
    }

    if (this.activeGalleryItem) {
      this.activeGalleryItem.isActive = false;
      this.activeGalleryItem.setAttribute('tabindex', '-1');
    }
  }

  /**
   * This method will handel on page load activity &
   * make visible 1st image/video
   */
  private onPageInit() {
    this.setMobile();

    this.setGalleryItem(this.images[this.activeIndex], this.activeIndex);
  }

  private setGalleryItem(selectedObj: ImageGallery, selectedIndex: number) {
    this.selectedItem = selectedObj;
    this.selectedIndex = selectedIndex;

    this.scrollIntoViewThumbnail();
  }

  /**
   * Get the thumbnail container from scroll.
   * For mobile with date variant container is span with cat-c-image-gallery__icon-container class and other cases container is within cat-image-gallery-thumbnail
   */
  private thumbnailContainer(): HTMLElement | null | undefined {
    if ((this.isMobile || this.size === 'sm') && this.slots.isEmpty()) {
      return this.shadowRoot?.querySelector('.cat-c-image-gallery__icon-container');
    } else {
      return this.gridPosition === 'bottom' || this.isMobile || this.size === 'sm'
        ? this._thumbnail
        : this._thumbnail?.shadowRoot?.querySelector('.cat-c-image-gallery-thumbnail');
    }
  }

  /**
   * Get the active element for scroll in to view
   */
  private activeEl(): HTMLElement | null | undefined {
    if ((this.isMobile || this.size === 'sm') && this.slots.isEmpty() && this._thumbnailItemsDefault) {
      return this._thumbnailItemsDefault[this.selectedIndex];
    } else if (this.slots.isEmpty() && this._thumbnailItemsDefault) {
      return this._thumbnailItemsDefault[this.selectedIndex]?.shadowRoot?.querySelector('.cat-c-image-gallery-thumbnail-item');
    } else {
      return this.activeThumbnailItem?.shadowRoot?.querySelector('.cat-c-image-gallery-thumbnail-item');
    }
  }

  /**
   * Scroll the active item in to the view
   * 1. Get the container
   * 2. Get the active thumbnail item
   * 3. Scroll in to view based on active thumbnail item and its container
   */
  private async scrollIntoViewThumbnail() {
    if (!this.hideThumbnails) {
      await this.updateComplete;
      /* 1 */
      const containerEl: HTMLElement | null | undefined = this.thumbnailContainer();
      /* 2 */
      const activeItemEl: HTMLElement | null | undefined = this.activeEl();
      /* 3 */
      if (containerEl && activeItemEl) {
        const direction = this.gridPosition === 'bottom' || this.isMobile || this.size === 'sm' ? 'horizontal' : 'vertical';
        const element = document?.defaultView?.getComputedStyle(activeItemEl);

        if (element) {
          scrollIntoView(activeItemEl, containerEl, 0, direction, 'smooth', parseInt(element.margin, 10));
        }
      }
    }
  }

  /**
   * Click handler
   */
  private clickHandler(selectedObj: ImageGallery, selectedIndex: number) {
    this.setGalleryItem(selectedObj, selectedIndex);
    this.activeIndex = selectedIndex;
    this.dispatch({ eventName: 'bl-thumbnail-select', detailObj: { selectedIndex } }) as BlocksThumbnailSelect;
  }

  /**
   * Disconnected callback lifecycle
   * 1) Remove event listeners
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.setMobile);
  }

  /**
   * Set GridPosition
   * 1) Loop through all the slotted Gallery Thumbnail items
   * 2) Sets the thumbnail position based on the gallery gridPosition property
   */
  private setGridPosition() {
    /* 1 */
    if (this.galleryThumbnails) {
      this.galleryThumbnails.forEach((item: BlocksImageGalleryThumbnail) => {
        /* 2 */
        if (this.gridPosition === 'bottom' || this.isMobile || this.size === 'sm') {
          item.position = null;
        } else {
          item.position = 'vertical';
        }
      });
    }
  }

  private setSmallView() {
    if (this._thumbnailItems) {
      this._thumbnailItems.forEach((item: BlocksImageGalleryThumbnailItem) => {
        if (this.isMobile || this.size === 'sm') {
          item.isSmall = true;
        } else {
          item.isSmall = false;
        }
      });
    }
  }

  private handleSlotChange() {
    this.createGallery();
    this.setGridPosition();
    this.setSmallView();
  }

  /**
   * Sets the keyboard navigation keys for Image Gallery
   */
  private setNavigationKeys() {
    if (this.gridPosition === 'bottom' || this.size === 'sm' || this.isMobile) {
      this.navigationKeys.prev = KEYS.LEFT_ARROW;
      this.navigationKeys.next = KEYS.RIGHT_ARROW;
    } else {
      this.navigationKeys.prev = KEYS.UP_ARROW;
      this.navigationKeys.next = KEYS.DOWN_ARROW;
    }
  }

  /**
   * Handles the keydown event for navigating through the image gallery.
   *
   * @param {KeyboardEvent} event - The keyboard event triggered by user interaction.
   *
   * The function supports the following keys:
   * - Right Arrow: Moves focus to the next thumbnail item.
   * - Left Arrow: Moves focus to the previous thumbnail item.
   * - Home: Moves focus to the first thumbnail item.
   * - End: Moves focus to the last thumbnail item.
   *
   * The function performs the following actions:
   * 1. Deactivates the previously selected item.
   * 2. Calculates the new focus index based on the pressed key.
   * 3. Updates the active index to the new focus index.
   * 4. Updates item accessibility.
   * 5. Dispatches a custom event 'bl-thumbnail-select' with the selected index.
   */
  private handleKeydown(event: KeyboardEvent): void {
    if ([this.navigationKeys.next, this.navigationKeys.prev, KEYS.HOME, KEYS.END].includes(event.key)) {
      event.preventDefault();
      this.deactivatePreviouslySelectedItem();
      const activeIndex = this.activeIndex;
      const thumbnailItemLength = this._thumbnailItems.length - 1;
      let focusIndex = 0;

      if ([this.navigationKeys.next].includes(event.key)) {
        focusIndex = (activeIndex + 1) % (thumbnailItemLength + 1); /*1*/
      } else if ([this.navigationKeys.prev].includes(event.key)) {
        focusIndex = activeIndex === 0 ? thumbnailItemLength : activeIndex - 1; /*2*/
      } else if (KEYS.HOME === event.key) {
        focusIndex = 0; /*3*/
      } else if (KEYS.END === event.key) {
        focusIndex = thumbnailItemLength; /*4*/
      }
      this.activeIndex = focusIndex;

      /*5*/
      this.updateItemAccessibility();

      this.dispatch({ eventName: 'bl-thumbnail-select', detailObj: { selectedIndex: this.activeIndex } }) as BlocksThumbnailSelect;
    }
  }

  /**
   * Updates the accessibility attributes for the thumbnail and gallery items.
   *
   * This method sets the `tabindex` and `aria-selected` attributes for each thumbnail item
   * based on the `activeIndex`. The active thumbnail item will have `tabindex` set to '0'
   * and `aria-selected` set to 'true', while the inactive items will have `tabindex` set to '-1'
   * and `aria-selected` set to 'false'.
   *
   * If there are multiple gallery items, it also sets the `tabindex` attribute for each gallery item
   * based on the `activeIndex`. The active gallery item will have `tabindex` set to '0', while the
   * inactive items will have `tabindex` set to '-1'.
   */
  private updateItemAccessibility() {
    this._thumbnailItems.forEach((item, index) => {
      if (index === this.activeIndex) {
        item.setAttribute('tabindex', '0');
        item.setAttribute('aria-selected', 'true');
        item.isActive = true;
      } else {
        item.setAttribute('tabindex', '-1');
        item.setAttribute('aria-selected', 'false');
        item.isActive = false;
      }
    });
    if (this._galleryItems.length > 1) {
      this._galleryItems.forEach((item, index) => {
        if (index === this.activeIndex) {
          item.setAttribute('tabindex', '0');
          item.isActive = true;
        } else {
          item.setAttribute('tabindex', '-1');
          item.isActive = false;
        }
      });
    }
  }

  /**
   * 1) Close all Thumbnail Item except for the one that was clicked
   */
  private setThumbnailItem(event: Event): void {
    event.preventDefault();
    const item = document.activeElement as BlocksImageGalleryThumbnailItem;
    if (!item.matches('cat-image-gallery-thumbnail-item')) {
      return;
    }
    if (this._thumbnailItems) {
      const activeIndex = this._thumbnailItems.indexOf(item);
      this.activeIndex = activeIndex;
      this.activateImageGallery();

      if (this.activeGalleryItem) {
        this.activeGalleryItem.isActive = true;
      }

      if (this.activeThumbnailItem) {
        this.activeThumbnailItem.isActive = true;
      }

      this.dispatch({ eventName: 'bl-thumbnail-select', detailObj: { selectedIndex: activeIndex } }) as BlocksThumbnailSelect;
    }
  }

  /**
   * Set Mobile size logic
   */
  private setMobile = () => {
    const width = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    if (width < this.mobileBreakPoint) {
      this.isMobile = true;
    } else {
      this.isMobile = false;
    }
    this.setGridPosition();
  };

  /**
   * returns the combined text of thumbnail text and ariatabLabel set for the thumbnail-item
   */
  private setArialLabelForThumbnailItem(thumbnailItemLabel: string, thumbnailItemText: string) {
    return [thumbnailItemLabel, thumbnailItemText].filter(Boolean).join(' ');
  }

  private renderThumbnails() {
    let thumbnails;
    if (!this.hideThumbnails) {
      if (this.isMobile || this.size === 'sm') {
        thumbnails = html`
          <div part="thumbnails-container" class="cat-c-image-gallery__icon-container">
            <div part="thumbnails-wrapper" class="cat-c-image-gallery__icon-wrapper" role="tablist" aria-label=${ifDefined(this.ariaTablistLabel)}>
              ${repeat(
                this.images,
                (image: ImageGallery) => image.src,
                (image: ImageGallery, index: number) => {
                  return html`
                    <div
                      part="thumbnails-link-wrapper"
                      class="cat-c-image-gallery__icon-link_wrapper"
                      role="tab"
                      aria-label=${this.setArialLabelForThumbnailItem(image.ariaTabLabel ?? '', this.images[index]?.thumbnailText ?? '')}
                      tabindex=${'0'}
                      aria-selected=${ifDefined(index === this.selectedIndex ? 'true' : 'false')}
                      @click=${() => {
                        this.clickHandler(image, index);
                      }}
                    >
                      <span
                        part="thumbnails-link"
                        class="${classMap({
                          'cat-c-image-gallery__icon-link': true,
                          'cat-is-active': index === this.selectedIndex
                        })}"
                        aria-label=${ifDefined(image.alt)}
                      >
                      </span>
                    </div>
                  `;
                }
              )}
            </div>
          </div>
        `;
      } else {
        thumbnails = html`
          <cat-image-gallery-thumbnail
            part="grid-item-wrapper"
            class="cat-c-image-gallery__grid"
            position=${ifDefined(this.gridPosition === 'bottom' ? undefined : 'vertical')}
            .arialabel=${ifDefined(this.ariaTablistLabel)}
          >
            ${repeat(
              this.images,
              (image: ImageGallery) => image.src,
              (image: ImageGallery, index: number) => {
                return html`
                  <cat-image-gallery-thumbnail-item
                    .src=${image.thumbnailSrc ? image.thumbnailSrc : image?.src}
                    .alt=${image.alt}
                    part="thumbnail-item"
                    ?isActive=${index === this.selectedIndex}
                    .arialabel=${this.setArialLabelForThumbnailItem(image.ariaTabLabel ?? '', this.images[index]?.thumbnailText ?? '')}
                    @click=${() => {
                      this.clickHandler(image, index);
                    }}
                  >
                  </cat-image-gallery-thumbnail-item>
                `;
              }
            )}
          </cat-image-gallery-thumbnail>
        `;
      }
    }

    return thumbnails;
  }

  private renderThumbnailText() {
    let thumbnailText;

    if (!this.hideThumbnailText && this.selectedItem?.thumbnailText) {
      thumbnailText = html`
        <div part="text-container" class="cat-c-image-gallery__thumbnail-text-container">
          <div part="text" class="cat-c-image-gallery__thumbnail-heading">${this.selectedItem?.thumbnailText}</div>
        </div>
      `;
    }

    return thumbnailText;
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-image-gallery', {
      'cat-c-image-gallery--sm': this.isMobile === true || this.size === 'sm',
      'cat-c-image-gallery--right': this.gridPosition === 'right',
      'cat-c-image-gallery--bottom': this.gridPosition === 'bottom',
      'cat-c-image-gallery--cover': this.objectFit === 'cover',
      'cat-c-image-gallery--fill': this.objectFit === 'fill',
      'cat-c-image-gallery--none': this.objectFit === 'none',
      'cat-c-image-gallery--custom-gallery': !this.slots.isEmpty()
    });

    return html`
      <div part="base" class="${componentClassNames}" @keydown=${this.handleKeydown}>
        <slot class="cat-c-image-gallery-slot" @slotchange=${this.handleSlotChange} @click=${this.setThumbnailItem}></slot>
        ${this.slots.isEmpty()
          ? html`
              ${this.renderThumbnails()}
              <div part="image-container" class="cat-c-image-gallery__img-container">
                <cat-image-gallery-item
                  part="gallery-item"
                  .data=${this.selectedItem}
                  .objectFit=${this.objectFit}
                  .gridPosition=${this.gridPosition}
                  .ariaroledescription=${ifDefined(this.selectedItem?.ariaTabPanelRoleDescription)}
                  .arialabel=${ifDefined(this.selectedItem?.ariaTabPanelLabel)}
                ></cat-image-gallery-item>
                ${this.renderThumbnailText()}
              </div>
            `
          : nothing}
      </div>
    `;
  }
}

if (customElements.get('cat-image-gallery') === undefined) {
  customElements.define('cat-image-gallery', BlocksImageGallery);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-image-gallery': BlocksImageGallery;
  }
}
